import { ColumnTable, Icon } from '@octano/global-ui';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationsKeys } from '../../../../locales/translations';
import { ChildSearchItem, ChildType } from '../types';

/**
 * HU 15571 — Columnas de la tabla local de items asignados (mezcla de
 * actividades y sub-agrupaciones). Sub-grupos muestran "N/A" en
 * Modalidad/Nivel/Créditos (resolución OPEN-2 acordada con producto).
 *
 * Para rows `type='group'` se renderiza un ícono "information" al lado
 * del nombre que dispara `onShowDetail(id)` para abrir el modal de
 * detalle (mismo UX que la columna "Detalle de agrupación" del listado).
 *
 * `onRemove` y `onShowDetail` deben venir memorizados desde el caller
 * para mantener estables las columnas.
 */
export function useColumnsItems(
  onRemove: (id: number, type: ChildType) => void,
  onShowDetail: (id: number) => void,
): ColumnTable<ChildSearchItem>[] {
  const { t } = useTranslation(TranslationsKeys.COURSE_GROUPS);

  return useMemo<ColumnTable<ChildSearchItem>[]>(() => {
    const na = t('items.table.notApplicable');
    return [
      {
        columnName: 'code',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center text-nowrap',
        headerText: t('items.table.code'),
        cellFormat: ({ row }) => row.code,
      },
      {
        columnName: 'name',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        headerText: t('items.table.name'),
        cellFormat: ({ row }) =>
          row.type === 'group' ? (
            <button
              type="button"
              onClick={() => onShowDetail(row.id)}
              aria-label={t('list.table.itemDetail')}
              className="btn p-0 d-inline-flex align-items-center"
              style={{ gap: 4 }}
            >
              <span>{row.name}</span>
              <Icon name="information" size="16px" color="primary" />
            </button>
          ) : (
            row.name
          ),
      },
      {
        columnName: 'school',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        headerText: t('items.table.school'),
        cellFormat: ({ row }) => row.school?.name ?? '-',
      },
      {
        columnName: 'modality',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        headerText: t('items.table.modality'),
        cellFormat: ({ row }) =>
          row.type === 'course' ? row.modality ?? '-' : na,
      },
      {
        columnName: 'academicLevel',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        headerText: t('items.table.academicLevel'),
        cellFormat: ({ row }) =>
          row.type === 'course' ? row.academicLevel ?? '-' : na,
      },
      {
        columnName: 'credits',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        headerText: t('items.table.credits'),
        cellFormat: ({ row }) => {
          if (row.type !== 'course') {
            return na;
          }
          if (row.credits === null || row.credits === undefined) {
            return na;
          }
          return String(row.credits);
        },
      },
      {
        columnName: 'action',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        headerText: t('items.table.action'),
        cellFormat: ({ row }: { row: ChildSearchItem }): ReactElement => (
          <button
            type="button"
            onClick={() => onRemove(row.id, row.type)}
            aria-label={t('items.table.remove')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <Icon name="trash" />
          </button>
        ),
      },
    ];
  }, [t, onRemove, onShowDetail]);
}
